import { apiRequest } from './config'

export interface DailyCountDto {
  date: string // ISO date
  count: number
}

export interface AdminUserDto {
  id: string
  name: string
  email: string
  plan: string
  joinedAt: string
  totalSpent: number
  emailVerified: boolean
}

export interface AdminUserWithUsageDto extends AdminUserDto {
  totalPodcasts: number
  draftPodcasts: number
  processingPodcasts: number
  completedPodcasts: number
  failedPodcasts: number
  totalLines: number // new: total lines across all podcasts
}

export interface AdminOverviewResponse {
  totalUsers: number
  paidUsers: number
  totalRevenue: number
  monthlyRevenue: number
  users: AdminUserDto[]
  registrationsHistory: DailyCountDto[]
  newSubscriptionsCount: number
  newTransactionsCount: number
  usersWithAnyPodcast: number
  usersWithCompletedPodcast: number
  signupToCreatedConversionRate: number
  signupToCompletedConversionRate: number
  createdToCompletedConversionRate: number
  // New: daily conversion history
  signupToCreatedHistory: DailyCountDto[]
  createdToCompletedHistory: DailyCountDto[]
  // New: series campaign metrics
  seriesTotalTriggered: number
  seriesEmail1Sent: number
  seriesEmail2Sent: number
  seriesEmail3Sent: number
  seriesCompleted: number
  seriesEligibleEmail1: number
  seriesEligibleEmail2: number
  seriesEligibleEmail3: number
}

export interface AdminUsersPageResponse {
  totalUsers: number
  page: number
  pageSize: number
  users: AdminUserWithUsageDto[]
}

export interface AdminPodcastLineDto {
  id: string
  host: string
  text: string
  audioFileUrl?: string
  startTimestampMs?: number
  endTimestampMs?: number
  sequenceOrder: number
  status: string
  createdAt: string
  updatedAt: string
}

export interface AdminPodcastDto {
  id: string
  title: string
  userName: string
  userEmail: string
  status: string
  sourceType: string
  sourcePayload: string
  additionalInstructions?: string
  style: string
  durationMinutes: number
  durationSeconds?: number
  languageIso: string
  speed: number
  volume: number
  emotion: string
  hostAVoiceId: string
  hostBVoiceId: string
  audioFileUrl?: string
  description?: string
  isGenerating: boolean
  createdAt: string
  updatedAt: string
  totalLines: number
  completedLines: number
  processingLines: number
  failedLines: number
  draftLines: number
  podcastLines: AdminPodcastLineDto[]
}

export interface AdminPodcastsPageResponse {
  totalPodcasts: number
  page: number
  pageSize: number
  podcasts: AdminPodcastDto[]
}

export const fetchAdminOverview = async (): Promise<AdminOverviewResponse> => {
  return apiRequest('/api/admin/overview')
}

export const fetchAdminUsersPage = async (page = 1, pageSize = 20): Promise<AdminUsersPageResponse> => {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
  return apiRequest(`/api/admin/users?${params.toString()}`)
}

export const fetchAdminPodcastsPage = async (page = 1, pageSize = 20, status?: string): Promise<AdminPodcastsPageResponse> => {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
  if (status && status !== 'All') {
    params.append('status', status)
  }
  return apiRequest(`/api/admin/podcasts?${params.toString()}`)
}

// Email marketing metrics
export interface EmailTypeMetricsDto {
  type: string
  trigger: string
  sentCount: number
}

export interface EmailMarketingMetricsResponse {
  subscribedUsers: number
  unsubscribedUsers: number
  subscribersGrowthLast30Days: DailyCountDto[]
  unsubscribesLast30Days: DailyCountDto[]
  emailTypes: EmailTypeMetricsDto[]
}

export const fetchAdminEmailMetrics = async (): Promise<EmailMarketingMetricsResponse> => {
  return apiRequest('/api/admin/email-metrics')
}

// Sequenzy sync
export interface SequenzySyncResponse {
  totalUsers: number
  syncedCount: number
  skippedCount: number
  failedCount: number
  errors: string[]
}

export const syncSequenzySubscribers = async (): Promise<SequenzySyncResponse> => {
  return apiRequest('/api/admin/sync-email-subscribers', { method: 'POST' })
}
